"use client";

import { APIUserData } from "@/app/types/user";
import {
  getAuthChallenge,
  verifySignedMessage,
  getUserData,
  removeUserData,
} from "@/app/utils/apis/apiService";
import type { WalletProviderType } from "@/app/utils/services/enum/wallet-provider-type";
import { WalletProviderType as WalletProvider } from "@/app/utils/services/enum/wallet-provider-type";
import { getWalletProviderType } from "@/app/utils/services/service/localStorageService";
import {
  clearWalletData,
  storeWalletData,
} from "@/app/utils/wallet/walletUtils";
import { useLocale } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { signLoginPayload, type LoginPayload } from "thirdweb/auth";
import { useActiveWallet, useDisconnect } from "thirdweb/react";
import type { Wallet, WalletId } from "thirdweb/wallets";
// Custom
import WalletService from "@/app/utils/wallet";
import { getStoredWalletData } from "@/app/utils/wallet/walletUtils";
import { AUTH_MODE } from "@/app/enum/wallet";
import { isWeb3AuthSignin } from "@/app/utils/services/helper/helper";

type WalletServiceContextProps = {
  walletService: Wallet<WalletId> | undefined;
  customWalletService: WalletService;
  userDetails: any;
  setUserDetails: React.Dispatch<React.SetStateAction<any>>;
  getConnectedWallet: () => string | null;
  loader: boolean;
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<boolean>>;
  initiateCustomAuthChallenge: (walletId: string) => Promise<void>;
  authMode: string;
  onDisconnect: () => void;
};

type UserDetails = {
  loggedIn: boolean;
  points: number;
  refUrl: string;
  walletId: string;
  // walletProviderType not exist in GET /user-data endpoint
  walletProviderType: string;
  email?: string;
};

const WalletServiceContext = createContext<
  WalletServiceContextProps | undefined
>(undefined);

export const useWalletService = () => {
  const context = useContext(WalletServiceContext);
  if (!context) {
    throw new Error(
      "useWalletService must be used within a WalletServiceProvider"
    );
  }
  return context;
};

type Props = {
  user: APIUserData | null;
  children: ReactNode;
  authMode: string;
};

const createLoginMessage = (payload: LoginPayload) => {
  const header = `${payload.domain} wants you to sign in with your Ethereum account:`;
  let prefix = [header, payload.address].join("\n");
  prefix = [prefix, payload.statement].join("\n\n");
  if (payload.statement) {
    prefix += "\n";
  }

  const suffixArray: string[] = [];
  if (payload.uri) {
    suffixArray.push(`URI: ${payload.uri}`);
  }

  suffixArray.push(`Version: ${payload.version}`);

  if (payload.chain_id) {
    suffixArray.push(`Chain ID: ${payload.chain_id}`);
  }

  suffixArray.push(`Nonce: ${payload.nonce}`);
  suffixArray.push(`Issued At: ${payload.issued_at}`);
  suffixArray.push(`Expiration Time: ${payload.expiration_time}`);

  if (payload.invalid_before) {
    suffixArray.push(`Not Before: ${payload.invalid_before}`);
  }

  if (payload.resources) {
    suffixArray.push(
      ["Resources:", ...payload.resources.map((x) => `- ${x}`)].join("\n")
    );
  }

  const suffix = suffixArray.join("\n");
  return [prefix, suffix].join("\n");
};

const WalletServiceProvider = ({ children, user, authMode }: Props) => {
  const locale = useLocale();
  const walletService = useActiveWallet();
  const { disconnect } = useDisconnect();

  const [loader, setLoader] = useState<boolean>(false);
  const [walletServiceInitialized, setWalletServiceInitialized] = useState(
    walletService ? true : false
  );
  const [customWalletService] = useState(new WalletService());
  const [openModal, setOpenModal] = useState<boolean>(false);

  const [userDetails, setUserDetails] = useState<UserDetails>({
    loggedIn: !!user,
    walletProviderType: "",
    walletId: user?.walletId ?? "",
    points: user?.points ?? 0,
    refUrl: user?.refUrl ?? "",
    email: user?.email ?? "",
  });

  const [, pathname] = usePathname().split("/");

  const getConnectedWallet = () => {
    return getWalletProviderType();
  };

  const cleanup = useCallback(async () => {
    clearWalletData();
    await removeUserData(locale);
  }, [locale]);

  const handleDisconnect = async () => {
    if (authMode == AUTH_MODE.Custom) {
      const storedProviderType = getStoredWalletData()?.walletProviderType;
      const providerType =
        (userDetails?.walletProviderType as WalletProviderType) ||
        (storedProviderType as WalletProviderType);
      await customWalletService.disconnectWallet(providerType);
    } else if (authMode == AUTH_MODE.Thirdweb) {
      if (walletService) {
        disconnect(walletService!);
      }
    }

    await cleanup();
    window.location.replace("/");
  };

  const initiateAuthChallenge = useCallback(async () => {
    if (authMode == AUTH_MODE.Custom) return;
    const userData = await getUserData(locale);
    if (userData) {
      setUserDetails({
        ...userDetails,
        points: userData?.points,
        refUrl: userData?.refUrl,
        loggedIn: true,
        walletProviderType: userData?.walletProviderType,
        walletId: userData?.walletId?.toLowerCase(),
      });
      setLoader(false);
      return;
    } else {
      clearWalletData();
      await removeUserData(locale);
      setLoader(false);
    }

    if (
      walletService &&
      walletService.id &&
      walletService?.getAccount() &&
      !userData
    ) {
      setLoader(true);
      const chainId = walletService?.getChain()?.id;
      const { data, error } = await getAuthChallenge(
        locale,
        walletService?.getAccount()?.address!,
        chainId
      );
      if (error || !data?.payload) {
        setLoader(false);
        return;
      }

      try {
        const signedPayload = await signLoginPayload({
          account: walletService?.getAccount()!,
          payload: data.payload,
        });

        const { data: tokenData } = await verifySignedMessage(
          locale,
          walletService?.getAccount()?.address!,
          signedPayload.signature,
          signedPayload.payload
        );

        if (tokenData?.success) {
          const userWalletData = await getUserData(locale);
          storeWalletData(
            walletService?.id as WalletProviderType,
            walletService?.getAccount()?.address?.toLowerCase() ?? ""
          );

          setUserDetails({
            loggedIn: true,
            walletProviderType: walletService?.id,
            walletId: walletService?.getAccount()?.address?.toLowerCase() ?? "",
            points: userWalletData?.points,
            refUrl: userWalletData?.refUrl,
          });
          setLoader(false);
        }
      } catch (signError) {
        if (walletService) {
          disconnect(walletService);
        }
        setLoader(false);
        return;
      }
    }

    if (walletServiceInitialized && !walletService) {
      clearWalletData();
      await removeUserData(locale);
      setUserDetails({
        loggedIn: false,
        walletProviderType: "",
        walletId: "",
        points: 0,
        refUrl: "",
      });
      setLoader(false);

      if (pathname === "account") {
        window.location.replace("/");
      }
    }

    if (walletService) {
      setWalletServiceInitialized(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cleanup,
    disconnect,
    locale,
    pathname,
    userDetails.loggedIn,
    walletService,
    walletServiceInitialized,
  ]);

  useEffect(() => {
    initiateAuthChallenge();
  }, [initiateAuthChallenge]);

  useEffect(() => {
    const initialize = async () => {
      if (authMode !== AUTH_MODE.Custom) {
        return;
      }

      let { walletProviderType } = getStoredWalletData();
      const sessionId = isWeb3AuthSignin();
      const isConnected = await customWalletService.checkIfConnected(
        sessionId
          ? WalletProvider.WEB3AUTH
          : (walletProviderType as WalletProviderType)
      );
      if (!walletProviderType) {
        walletProviderType = getStoredWalletData()?.walletProviderType;
      }

      if (isConnected) {
        const userData = await getUserData(locale);
        if (userData) {
          setUserDetails({
            ...userDetails,
            points: userData?.points,
            refUrl: userData?.refUrl,
            loggedIn: true,
            walletProviderType: userData?.walletProviderType,
            walletId: userData?.walletId?.toLowerCase(),
          });
          setLoader(false);
          return;
        } else {
          setUserDetails({
            loggedIn: false,
            walletProviderType: "",
            walletId: "",
            points: 0,
            refUrl: "",
          });
          clearWalletData();
          setLoader(false);
        }
      } else {
        setUserDetails({
          loggedIn: false,
          walletProviderType: "",
          walletId: "",
          points: 0,
          refUrl: "",
        });
        clearWalletData();
        await removeUserData(locale);
        setLoader(false);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initiateCustomAuthChallenge = useCallback(
    async (walletId: string) => {
      if (authMode == AUTH_MODE.Thirdweb) return;

      if (!walletId) {
        return;
      }

      setLoader(true);
      const walletProviderType = getStoredWalletData()?.walletProviderType;
      if (!walletProviderType) {
        setLoader(false);
        return;
      }
      let chainId: number | undefined;
      if (walletProviderType === WalletProvider.COINBASE) {
        chainId = 8453;
      } else if (walletProviderType === WalletProvider.WALLET_CONNECT) {
        chainId = 1;
      }
      const { data, error } = await getAuthChallenge(locale, walletId, chainId);
      if (error || !data?.payload) {
        setLoader(false);
        return;
      }
      try {
        const signature = await customWalletService.signTransaction(
          walletId,
          createLoginMessage(data.payload),
          walletProviderType as WalletProviderType
        );

        const { data: tokenData } = await verifySignedMessage(
          locale,
          walletId,
          signature,
          data.payload
        );

        if (tokenData?.success) {
          const userWalletData = await getUserData(locale);

          setUserDetails({
            loggedIn: true,
            walletProviderType: walletProviderType!,
            walletId: userWalletData?.walletId?.toLowerCase() ?? "",
            points: userWalletData?.points,
            refUrl: userWalletData?.refUrl,
          });
          setLoader(false);
        }
      } catch (error) {
      } finally {
        setLoader(false);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [authMode, customWalletService, locale]
  );

  return (
    <WalletServiceContext.Provider
      value={{
        walletService,
        userDetails,
        setUserDetails,
        loader,
        getConnectedWallet,
        customWalletService,
        openModal,
        setOpenModal,
        initiateCustomAuthChallenge,
        authMode: authMode || "",
        onDisconnect: handleDisconnect,
      }}
    >
      {children}
    </WalletServiceContext.Provider>
  );
};

export default WalletServiceProvider;
